'use client';

import type {
  PublicShareDetail,
  ShareContentPayload,
} from '@/app/actions/share';
import { ReferenceItem } from '@/app/services/llm';
import { DeepResearchPreview } from '@/components/deep-research/deep-research-preview';
import { DeepResearchReport } from '@/components/deep-research/deep-research-report';
import PaperLink from '@/components/paper-link';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  getPublicDeepResearchPreviewRef,
  stripLeadingTitle,
} from '@/components/deep-research/public-deep-research-reader-utils';
import { getReferenceDetailUrl } from '@/lib/reference-detail-url';
import { cn } from '@/lib/utils';
import { useMemo, useState } from 'react';

function resolveContent(detail: PublicShareDetail): ShareContentPayload | null {
  if (detail.source_type !== 'DEEP_RESEARCH') {
    return null;
  }
  return detail.content_json ?? null;
}

export default function PublicDeepResearchReader({
  detail,
  className,
}: {
  detail: PublicShareDetail;
  className?: string;
}) {
  const content = resolveContent(detail);
  const [previewRef, setPreviewRef] = useState<ReferenceItem | null>(null);
  const references = useMemo(
    () => content?.ref_docs ?? [],
    [content?.ref_docs],
  );
  const reportContent = stripLeadingTitle(
    content?.content ?? '',
    detail.title ?? '',
  );

  const openReferencePreview = (index: number) => {
    const ref = getPublicDeepResearchPreviewRef(references, index);
    if (!ref) return;
    setPreviewRef(ref);
  };

  return (
    <div className={cn('mx-auto max-w-4xl', className)}>
      <article className="min-w-0">
        <div>
          <DeepResearchReport
            reportContent={reportContent}
            references={references}
            writing={false}
            done
            isCompleted
            nextStep=""
            scrollContainer={null}
            onRefClick={openReferencePreview}
          />
        </div>

        {references.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 text-xl font-semibold">参考文献</h2>
            <div className="space-y-3">
              {references.map((ref, index) => {
                const detailUrl = getReferenceDetailUrl(ref);

                return (
                  <div key={`${ref.pmid}-${index}`} id={`share-ref-${index}`}>
                    <PaperLink
                      item={ref}
                      index={index}
                      showDetails
                      href={detailUrl || undefined}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </article>

      <Sheet
        open={Boolean(previewRef)}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewRef(null);
          }
        }}
      >
        <SheetContent
          side="right"
          className="w-full overflow-hidden p-0 sm:max-w-150"
          showCloseButton={false}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>摘要详情</SheetTitle>
          </SheetHeader>
          {previewRef ? (
            <div className="h-full">
              <DeepResearchPreview
                current={previewRef}
                onClose={() => setPreviewRef(null)}
              />
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
